// frontend/src/screens/cart/CartMobileScreen.js

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import clsx from "clsx";
import {
  AlertCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Info,
  Minus,
  PackageCheck,
  Plus,
  PoundSterling,
  ShieldCheck,
  ShoppingBag,
  Trash2,
  XCircle,
} from "lucide-react";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import {
  clearCart,
  decrementCartItem,
  getCart,
  incrementCartItem,
  refreshCart,
  removeCartItem,
  removePriceChangedItems,
  removeUnavailableItems,
  resetCartError,
  resetCartMeta,
  setCartItemQty,
  validateCart,
} from "../../actions/cartActions";

import Meta from "../../components/Meta";
import { getSafeUserMessage } from "../../utils/errorMessages";
import "./CartMobileScreen.css";

const FALLBACK_IMAGE = "https://placehold.co/640x760?text=Academy+Costumes+London";

const BLOCKING_STOCK_STATUSES = new Set([
  "out_of_stock",
  "requested_qty_exceeds_available",
  "shared_stock_exhausted_in_cart",
  "shared_stock_blocked_by_cart",
  "unavailable",
]);

const NON_EDITABLE_STOCK_STATUSES = new Set(["out_of_stock", "unavailable"]);

const BLOCKING_STOCK_CODES = new Set([
  "OUT_OF_STOCK",
  "INSUFFICIENT_STOCK",
  "INSUFFICIENT_SHARED_STOCK",
  "REQUESTED_QTY_EXCEEDS_AVAILABLE",
  "SHARED_STOCK_EXHAUSTED_IN_CART",
  "SHARED_STOCK_BLOCKED_BY_CART",
  "UNAVAILABLE",
]);

const SUCCESS_TOAST_ACTIONS = new Set([
  "REMOVE_UNAVAILABLE",
  "REMOVE_PRICE_CHANGED",
  "REFRESH",
  "CLEAR",
  "REMOVE_ITEM",
  "SET_ITEM_QTY",
  "INCREMENT_ITEM",
  "DECREMENT_ITEM",
  "ADD_ITEM",
]);

function formatMoney(value = 0, currency = "GBP") {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
    }).format(Number(value || 0));
  } catch {
    return `£${Number(value || 0).toFixed(2)}`;
  }
}

function toWholeQty(value, fallback = 1) {
  const number = Math.floor(Number(value));

  if (!Number.isFinite(number) || number < 1) return fallback;

  return number;
}

function clampQty(value, min = 1, max = Number.POSITIVE_INFINITY) {
  const safeMin = Math.max(1, toWholeQty(min, 1));

  const safeMax = Number.isFinite(Number(max))
    ? Math.max(safeMin, Math.floor(Number(max)))
    : Number.POSITIVE_INFINITY;

  return Math.max(safeMin, Math.min(toWholeQty(value, safeMin), safeMax));
}

function safeText(value = "") {
  return String(value || "").trim();
}

function normalizeImage(src = "") {
  const safe = safeText(src);
  return safe || FALLBACK_IMAGE;
}

function normalizeStatus(value = "") {
  return safeText(value).toLowerCase();
}

function normalizeCode(value = "") {
  return safeText(value).toUpperCase();
}

function getStockStatus(item = {}) {
  return normalizeStatus(
    item?.stockStatus ||
      item?.status ||
      item?.unavailableCode ||
      item?.stockStatusCode,
  );
}

function getStockCode(item = {}) {
  return normalizeCode(item?.stockStatusCode || item?.unavailableCode || "");
}

function getStockReason(item = {}) {
  return safeText(
    item?.stockStatusReason ||
      item?.unavailableReason ||
      item?.disabledReason ||
      "",
  );
}

function getRemainingAddableQty(item = {}) {
  return Math.max(0, Math.floor(Number(item?.remainingAddableQty || 0)));
}

function getMaxQty(item = {}) {
  const maxQty = Number(item?.maxQty);
  const availableAtRefresh = Number(item?.availableAtRefresh);
  const qty = Number(item?.qty || 0);

  if (Number.isFinite(maxQty) && maxQty >= 0) return Math.floor(maxQty);

  if (Number.isFinite(availableAtRefresh) && availableAtRefresh >= 0) {
    return Math.floor(availableAtRefresh);
  }

  return Math.max(1, Math.floor(qty || 1));
}

function itemHasBlockingStatus(item = {}) {
  return (
    BLOCKING_STOCK_STATUSES.has(getStockStatus(item)) ||
    BLOCKING_STOCK_CODES.has(getStockCode(item)) ||
    item?.isAvailable === false
  );
}

function itemCanIncrement(item = {}) {
  if (!item?.isAvailable) return false;
  if (item?.priceChanged) return false;
  if (itemHasBlockingStatus(item)) return false;
  if (item?.canIncrement === false) return false;
  if (item?.canAddMore === false) return false;
  if (item?.canAddToCart === false) return false;

  return (
    getRemainingAddableQty(item) > 0 && Number(item?.qty || 0) < getMaxQty(item)
  );
}

function itemCanEditQty(item = {}) {
  if (item?.priceChanged) return false;

  const maxQty = getMaxQty(item);
  if (maxQty < 1) return false;

  const status = getStockStatus(item);

  if (NON_EDITABLE_STOCK_STATUSES.has(status)) return false;

  return Boolean(item?.isAvailable || maxQty >= 1);
}

function buildProductLink(item = {}) {
  const shopKey = encodeURIComponent(safeText(item?.shopKey));

  const identifier = encodeURIComponent(
    safeText(item?.slug || item?.productSlug || item?.productId),
  );

  if (!shopKey || !identifier) return "/shop";

  return `/shop/${shopKey}/product/${identifier}`;
}

function getLineDescriptor(item = {}) {
  const bits = [];

  if (item?.variantName) bits.push(item.variantName);
  if (item?.unitLabel) bits.push(`Per ${item.unitLabel}`);
  if (item?.color) bits.push(item.color);
  if (item?.size) bits.push(item.size);
  if (item?.material) bits.push(item.material);
  if (item?.length) bits.push(item.length);

  return bits.map(safeText).filter(Boolean).join(" · ");
}

function isSharedTextileLine(item = {}) {
  return (
    item?.productType === "textile" &&
    safeText(item?.variantType).toLowerCase() !== "sample"
  );
}

function getLineAlert(item = {}, currency = "GBP") {
  const status = getStockStatus(item);
  const code = getStockCode(item);
  const reason = getStockReason(item);

  if (item?.priceChanged) {
    return {
      tone: "warning",
      icon: PoundSterling,
      title: "Price updated",
      message: `Changed from ${formatMoney(
        item.previousUnitPrice || 0,
        currency,
      )} to ${formatMoney(item.unitPrice || 0, currency)}.`,
    };
  }

  if (
    status === "shared_stock_blocked_by_cart" ||
    status === "shared_stock_exhausted_in_cart" ||
    code === "INSUFFICIENT_SHARED_STOCK" ||
    status.includes("shared") ||
    /shared|roll stock/i.test(reason)
  ) {
    return {
      tone: "warning",
      icon: AlertCircle,
      title:
        status === "shared_stock_blocked_by_cart"
          ? "Shared roll stock"
          : "Roll stock reserved",
      message:
        reason ||
        "This textile selection shares stock with another basket line.",
    };
  }

  if (status === "max_in_cart" || code === "MAX_IN_CART") {
    return {
      tone: "info",
      icon: Info,
      title: "Maximum reserved",
      message:
        reason ||
        "You have the available quantity for this option in your basket.",
    };
  }

  if (
    status === "requested_qty_exceeds_available" ||
    code === "REQUESTED_QTY_EXCEEDS_AVAILABLE" ||
    code === "INSUFFICIENT_STOCK"
  ) {
    return {
      tone: "warning",
      icon: AlertCircle,
      title: "Adjust quantity",
      message: reason || `This line can currently be held at ${getMaxQty(item)}.`,
    };
  }

  if (
    !item?.isAvailable ||
    status === "out_of_stock" ||
    status === "unavailable" ||
    code === "OUT_OF_STOCK" ||
    code === "UNAVAILABLE"
  ) {
    return {
      tone: "danger",
      icon: XCircle,
      title: status === "out_of_stock" ? "Sold out" : "Unavailable",
      message:
        reason ||
        "This item is not available right now. Remove it to continue checkout.",
    };
  }

  const remaining = getRemainingAddableQty(item);

  if (remaining === 0 && Number(item?.qty || 0) >= getMaxQty(item)) {
    return {
      tone: "info",
      icon: Info,
      title: "All available stock reserved",
      message: "You have the available quantity in your basket.",
    };
  }

  if (isSharedTextileLine(item) && remaining > 0 && remaining <= 2) {
    return {
      tone: "info",
      icon: Info,
      title: "Low roll stock",
      message: `Only ${remaining} more can be added for this textile selection.`,
    };
  }

  if (remaining > 0 && remaining <= 2) {
    return {
      tone: "info",
      icon: Info,
      title: "Low stock",
      message: `Only ${remaining} more can be added.`,
    };
  }

  return null;
}

function getQtyHelper(item = {}) {
  const status = getStockStatus(item);
  const code = getStockCode(item);
  const reason = getStockReason(item);
  const remaining = getRemainingAddableQty(item);
  const maxQty = getMaxQty(item);
  const qty = Number(item?.qty || 0);

  if (item?.priceChanged) return "Review the updated price before checkout.";

  if (status === "out_of_stock" || status === "unavailable" || maxQty < 1) {
    return reason || "Remove this line to continue checkout.";
  }

  if (status === "shared_stock_blocked_by_cart") {
    return reason || "Another cut in your basket is using the same roll stock.";
  }

  if (status === "shared_stock_exhausted_in_cart") {
    return (
      reason ||
      "The available roll stock for this fabric is already reserved in your basket."
    );
  }

  if (status === "max_in_cart" || code === "MAX_IN_CART") {
    return (
      reason ||
      "You have the available quantity for this option in your basket."
    );
  }

  if (
    status === "requested_qty_exceeds_available" ||
    code === "REQUESTED_QTY_EXCEEDS_AVAILABLE" ||
    code === "INSUFFICIENT_STOCK"
  ) {
    return reason || `Reduce this line to ${maxQty} or less.`;
  }

  if (remaining > 0) return `You can add ${remaining} more.`;

  if (qty >= maxQty) {
    return "All available quantity is already in your basket.";
  }

  return "";
}

function getSharedStockRows(item = {}) {
  const shared = item?.sharedStock || {};
  if (!shared?.isShared) return [];

  const rows = [];

  if (shared.baseMetres != null) {
    rows.push(["Roll stock", `${Number(shared.baseMetres || 0)} m`]);
  }

  if (shared.consumedByThisLineMetres != null) {
    rows.push([
      "This line uses",
      `${Number(shared.consumedByThisLineMetres || 0)} m`,
    ]);
  }

  if (shared.consumedByOtherLinesMetres != null) {
    rows.push([
      "Related basket cuts use",
      `${Number(shared.consumedByOtherLinesMetres || 0)} m`,
    ]);
  }

  if (shared.remainingMetres != null) {
    rows.push(["Roll remaining", `${Number(shared.remainingMetres || 0)} m`]);
  }

  return rows;
}

function normalizeSummary(summary = {}, cart = {}) {
  const cartUi = cart?.ui || {};
  const summaryUi = summary?.ui || {};

  const itemCount = Number(summary?.itemCount ?? cart?.itemCount ?? 0);

  const distinctItemCount = Number(
    summary?.distinctItemCount ?? cart?.distinctItemCount ?? 0,
  );

  const subtotal = Number(summary?.subtotal ?? cart?.subtotal ?? 0);

  const hasUnavailableItems = Boolean(
    summary?.hasUnavailableItems ?? cart?.hasUnavailableItems,
  );

  const hasPriceChanges = Boolean(
    summary?.hasPriceChanges ?? cart?.hasPriceChanges,
  );

  const readyForCheckout = Boolean(
    summary?.readyForCheckout ?? cart?.readyForCheckout,
  );

  const hasItems = Boolean(
    summaryUi?.hasItems ?? cartUi?.hasItems ?? itemCount > 0,
  );

  const unavailableItemCount = Number(
    summaryUi?.unavailableItemCount ??
      cartUi?.unavailableItemCount ??
      (hasUnavailableItems ? 1 : 0),
  );

  const priceChangedItemCount = Number(
    summaryUi?.priceChangedItemCount ??
      cartUi?.priceChangedItemCount ??
      (hasPriceChanges ? 1 : 0),
  );

  const blockingIssueCount = Number(
    summaryUi?.blockingIssueCount ??
      cartUi?.blockingIssueCount ??
      unavailableItemCount + priceChangedItemCount,
  );

  const canCheckout = Boolean(
    summaryUi?.actions?.canCheckout ??
      cartUi?.actions?.canCheckout ??
      (hasItems && readyForCheckout && blockingIssueCount === 0),
  );

  return {
    itemCount,
    distinctItemCount,
    subtotal,
    hasUnavailableItems,
    hasPriceChanges,
    readyForCheckout,
    ui: {
      status:
        summaryUi?.status ||
        cartUi?.status ||
        (canCheckout ? "ready" : hasItems ? "attention" : "empty"),
      hasItems,
      blockingIssueCount,
      unavailableItemCount,
      priceChangedItemCount,
      actions: {
        canCheckout,
        canRemoveUnavailableItems: Boolean(
          summaryUi?.actions?.canRemoveUnavailableItems ??
            cartUi?.actions?.canRemoveUnavailableItems ??
            unavailableItemCount > 0,
        ),
        canRemovePriceChangedItems: Boolean(
          summaryUi?.actions?.canRemovePriceChangedItems ??
            cartUi?.actions?.canRemovePriceChangedItems ??
            priceChangedItemCount > 0,
        ),
      },
    },
  };
}

function getIssueBanner({ unavailableCount, priceChangedCount }) {
  if (unavailableCount <= 0 && priceChangedCount <= 0) return null;

  return {
    tone: "warning",
    icon: AlertCircle,
    title: "Basket needs attention",
    message:
      "Some items have changed availability or price. Resolve these before checkout.",
  };
}

const CartMobileSkeleton = memo(function CartMobileSkeleton() {
  return (
    <main className="cart-mobile cart-mobile--state">
      <div className="cart-mobile__skeleton-head">
        <span />
        <strong />
        <em />
      </div>

      <div className="cart-mobile__skeleton-list">
        {Array.from({ length: 3 }).map((_, index) => (
          <div className="cart-mobile__skeleton-item" key={index}>
            <span />
            <div>
              <strong />
              <em />
              <small />
            </div>
          </div>
        ))}
      </div>
    </main>
  );
});

const EmptyCartMobileState = memo(function EmptyCartMobileState() {
  return (
    <section className="cart-mobile__empty">
      <div className="cart-mobile__empty-icon">
        <ShoppingBag size={28} />
      </div>

      <p className="cart-mobile__empty-eyebrow">Academy Costumes London</p>

      <h1>Your basket is empty</h1>

      <p>
        Explore fabrics, trims and ready-made pieces, then add them here to
        continue to checkout.
      </p>

      <Link to="/" className="cart-mobile__empty-cta">
        Continue shopping
        <ArrowRight size={16} />
      </Link>
    </section>
  );
});

const CartMobileIssueBanner = memo(function CartMobileIssueBanner({
  banner,
  cartBusy,
  pending,
  unavailableCount,
  priceChangedCount,
  canRemoveUnavailable,
  canRemovePriceChanged,
  onRemoveUnavailable,
  onRemovePriceChanged,
}) {
  if (!banner) return null;

  const BannerIcon = banner.icon || AlertCircle;

  return (
    <section
      className={clsx(
        "cart-mobile__issue",
        banner.tone === "warning" && "is-warning",
      )}
      aria-live="polite"
    >
      <div className="cart-mobile__issue-main">
        <BannerIcon size={17} />

        <div>
          <strong>{banner.title}</strong>
          <p>{banner.message}</p>
        </div>
      </div>

      {unavailableCount > 0 || priceChangedCount > 0 ? (
        <div className="cart-mobile__issue-actions">
          {unavailableCount > 0 ? (
            <button
              type="button"
              onClick={onRemoveUnavailable}
              disabled={
                cartBusy ||
                !canRemoveUnavailable ||
                Boolean(pending?.cleanupUnavailable)
              }
            >
              Remove unavailable
            </button>
          ) : null}

          {priceChangedCount > 0 ? (
            <button
              type="button"
              onClick={onRemovePriceChanged}
              disabled={
                cartBusy ||
                !canRemovePriceChanged ||
                Boolean(pending?.cleanupPriceChanged)
              }
            >
              Remove price changes
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
});

const CartMobileLineItem = memo(function CartMobileLineItem({
  item,
  currency,
  draftQty,
  itemBusy,
  onDraftChange,
  onCommitQty,
  onIncrement,
  onDecrement,
  onRemove,
}) {
  const [showDetails, setShowDetails] = useState(false);

  const itemId = safeText(item?._id);
  const productLink = useMemo(() => buildProductLink(item), [item]);
  const descriptor = useMemo(() => getLineDescriptor(item), [item]);

  const lineAlert = useMemo(
    () => getLineAlert(item, currency),
    [item, currency],
  );

  const qtyHelper = useMemo(() => getQtyHelper(item), [item]);
  const sharedRows = useMemo(() => getSharedStockRows(item), [item]);

  const maxQty = getMaxQty(item);
  const inputMaxQty = Math.max(1, maxQty);

  const canIncrease = Boolean(itemCanIncrement(item) && !itemBusy);
  const canEditQty = Boolean(itemCanEditQty(item) && !itemBusy);
  const canDecrease = Boolean(Number(item?.qty || 0) > 1 && canEditQty);

  const AlertIcon = lineAlert?.icon || Info;

  const handleImageError = useCallback((event) => {
    if (event.currentTarget.dataset.fallbackApplied === "true") return;

    event.currentTarget.dataset.fallbackApplied = "true";
    event.currentTarget.src = FALLBACK_IMAGE;
  }, []);

  const handleDraftChange = useCallback(
    (event) => {
      onDraftChange(itemId, event.target.value, inputMaxQty);
    },
    [inputMaxQty, itemId, onDraftChange],
  );

  const handleCommit = useCallback(() => {
    onCommitQty(item);
  }, [item, onCommitQty]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleCommit();
      }

      if (event.key === "Escape") {
        event.preventDefault();
        onDraftChange(itemId, String(item.qty || 1), inputMaxQty);
      }
    },
    [handleCommit, inputMaxQty, item.qty, itemId, onDraftChange],
  );

  return (
    <article
      className={clsx(
        "cart-mobile__item",
        !item?.isAvailable && "has-issue",
        item?.priceChanged && "has-price-change",
      )}
    >
      <Link to={productLink} className="cart-mobile__item-image-link">
        <img
          src={normalizeImage(item.image)}
          alt={item.productName || "Product"}
          loading="lazy"
          onError={handleImageError}
        />
      </Link>

      <div className="cart-mobile__item-body">
        <div className="cart-mobile__item-top">
          <div className="cart-mobile__item-copy">
            <Link to={productLink} className="cart-mobile__item-title">
              {item.productName || "Product"}
            </Link>

            {descriptor ? (
              <p className="cart-mobile__item-descriptor">{descriptor}</p>
            ) : null}

            <div className="cart-mobile__item-price">
              {item.priceChanged && item.previousUnitPrice ? (
                <span>{formatMoney(item.previousUnitPrice, currency)}</span>
              ) : null}

              <strong>{formatMoney(item.unitPrice, currency)}</strong>

              {item.unitLabel ? <em>per {item.unitLabel}</em> : null}
            </div>
          </div>

          <button
            type="button"
            className="cart-mobile__remove"
            onClick={() => onRemove(itemId)}
            disabled={itemBusy || !itemId}
            aria-label={`Remove ${item.productName || "item"}`}
          >
            <Trash2 size={15} />
          </button>
        </div>

        {lineAlert ? (
          <div
            className={clsx(
              "cart-mobile__line-alert",
              lineAlert.tone === "warning" && "is-warning",
              lineAlert.tone === "danger" && "is-danger",
              lineAlert.tone === "info" && "is-info",
            )}
          >
            <AlertIcon size={15} />

            <div>
              <strong>{lineAlert.title}</strong>
              <p>{lineAlert.message}</p>
            </div>
          </div>
        ) : null}

        <div className="cart-mobile__item-bottom">
          <div className="cart-mobile__qty">
            <span>Quantity</span>

            <div className="cart-mobile__qty-control">
              <button
                type="button"
                onClick={() => onDecrement(itemId)}
                disabled={!canDecrease || !itemId}
                aria-label="Decrease quantity"
              >
                <Minus size={13} />
              </button>

              <input
                type="text"
                inputMode="numeric"
                value={draftQty}
                disabled={!canEditQty}
                onChange={handleDraftChange}
                onBlur={handleCommit}
                onKeyDown={handleKeyDown}
                aria-label="Cart line quantity"
              />

              <button
                type="button"
                onClick={() => onIncrement(itemId)}
                disabled={!canIncrease || !itemId}
                aria-label="Increase quantity"
              >
                <Plus size={13} />
              </button>
            </div>
          </div>

          <div className="cart-mobile__line-total">
            <span>Line total</span>
            <strong>{formatMoney(item.lineTotal, currency)}</strong>
          </div>
        </div>

        {qtyHelper ? <p className="cart-mobile__qty-note">{qtyHelper}</p> : null}

        {sharedRows.length ? (
          <div className="cart-mobile__details">
            <button
              type="button"
              className="cart-mobile__details-toggle"
              onClick={() => setShowDetails((prev) => !prev)}
              aria-expanded={showDetails}
            >
              <PackageCheck size={14} />
              <span>Shared stock details</span>
              {showDetails ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            </button>

            {showDetails ? (
              <div className="cart-mobile__details-grid">
                {sharedRows.map(([label, value]) => (
                  <div key={`${itemId}-${label}`}>
                    <span>{label}</span>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </article>
  );
});

const CartMobileSummary = memo(function CartMobileSummary({
  summary,
  currency,
  unavailableCount,
  priceChangedCount,
  checkoutDisabled,
  cartBusy,
  pending,
  validation,
  onCheckout,
  onRefresh,
  onClear,
}) {
  const lineLabel = summary?.distinctItemCount === 1 ? "line" : "lines";

  return (
    <section className="cart-mobile__summary" aria-label="Order summary">
      <div className="cart-mobile__summary-head">
        <div>
          <p>Secure checkout</p>
          <h2>Order summary</h2>
        </div>

        <span>
          {summary?.distinctItemCount || 0} {lineLabel}
        </span>
      </div>

      <div className="cart-mobile__summary-rows">
        <div>
          <span>Items</span>
          <strong>{summary?.itemCount || 0}</strong>
        </div>

        <div>
          <span>Subtotal</span>
          <strong>{formatMoney(summary?.subtotal || 0, currency)}</strong>
        </div>
      </div>

      {unavailableCount > 0 || priceChangedCount > 0 ? (
        <div className="cart-mobile__summary-issues">
          {unavailableCount > 0 ? (
            <p>
              <AlertCircle size={14} />
              <span>{unavailableCount} unavailable line(s)</span>
            </p>
          ) : null}

          {priceChangedCount > 0 ? (
            <p>
              <PoundSterling size={14} />
              <span>{priceChangedCount} price change(s)</span>
            </p>
          ) : null}
        </div>
      ) : null}

      <button
        type="button"
        className={clsx(
          "cart-mobile__checkout",
          checkoutDisabled && "is-disabled",
        )}
        disabled={checkoutDisabled}
        onClick={onCheckout}
      >
        <span>Proceed to checkout</span>
        <ArrowRight size={16} />
      </button>

      <div className="cart-mobile__trust">
        <p>
          <ShieldCheck size={15} />
          <span>Validated against live stock</span>
        </p>

        <p>
          <ShieldCheck size={15} />
          <span>Shared textile stock checked</span>
        </p>
      </div>

      {validation?.issues?.length ? (
        <div className="cart-mobile__validation">
          <h3>Latest validation</h3>
          <p>{validation.message}</p>

          {validation.issues.slice(0, 4).map((issue, index) => (
            <span key={`${issue.code || "issue"}-${index}`}>
              {issue.message}
            </span>
          ))}
        </div>
      ) : null}
    </section>
  );
});

const CartMobileStickyBar = memo(function CartMobileStickyBar({
  summary,
  currency,
  checkoutDisabled,
  onCheckout,
}) {
  return (
    <div className="cart-mobile__sticky" aria-label="Mobile checkout bar">
      <div>
        <strong>{formatMoney(summary?.subtotal || 0, currency)}</strong>
        <span>{summary?.itemCount || 0} item(s)</span>
      </div>

      <button type="button" disabled={checkoutDisabled} onClick={onCheckout}>
        Checkout
        <ArrowRight size={16} />
      </button>
    </div>
  );
});

export default function CartMobileScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const cartState = useSelector((state) => state.cart || {}, shallowEqual);

  const {
    cart,
    summary,
    validation,
    loading,
    error,
    message,
    pending = {},
    lastAction,
  } = cartState;

  const [qtyDrafts, setQtyDrafts] = useState({});
  const lastSuccessToastKeyRef = useRef("");
  const lastErrorToastKeyRef = useRef("");

  useEffect(() => {
    dispatch(getCart());

    return () => {
      dispatch(resetCartError());
      dispatch(resetCartMeta());
    };
  }, [dispatch]);

  const items = useMemo(
    () => (Array.isArray(cart?.items) ? cart.items : []),
    [cart?.items],
  );

  const itemSignature = useMemo(
    () =>
      items
        .map((item) =>
          [
            item?._id,
            item?.qty,
            item?.updatedAt,
            item?.isAvailable,
            item?.priceChanged,
            item?.remainingAddableQty,
            item?.canIncrement,
            item?.canAddMore,
            item?.canAddToCart,
            item?.stockStatus,
            item?.stockStatusCode,
          ].join(":"),
        )
        .join("|"),
    [items],
  );

  useEffect(() => {
    setQtyDrafts(() => {
      const next = {};

      items.forEach((item) => {
        const key = safeText(item?._id);
        if (!key) return;
        next[key] = String(item?.qty || 1);
      });

      return next;
    });
  }, [itemSignature, items]);

  useEffect(() => {
    if (!message) return;

    const toastKey = `${lastAction}:${message}`;
    if (lastSuccessToastKeyRef.current === toastKey) return;

    lastSuccessToastKeyRef.current = toastKey;

    if (SUCCESS_TOAST_ACTIONS.has(lastAction)) {
      toast.success(message);
    }
  }, [message, lastAction]);

  useEffect(() => {
    if (!error) return;

    const toastKey = String(error);
    if (lastErrorToastKeyRef.current === toastKey) return;

    lastErrorToastKeyRef.current = toastKey;
    toast.error(error);
  }, [error]);

  const currency = safeText(cart?.currency || "GBP");
  const hasItems = items.length > 0;

  const resolvedSummary = useMemo(
    () => normalizeSummary(summary, cart),
    [summary, cart],
  );

  const cartBusy = Boolean(loading);

  const unavailableCount = Number(
    resolvedSummary?.ui?.unavailableItemCount || 0,
  );

  const priceChangedCount = Number(
    resolvedSummary?.ui?.priceChangedItemCount || 0,
  );

  const checkoutDisabled = Boolean(
    cartBusy ||
      pending?.validate ||
      !resolvedSummary?.ui?.actions?.canCheckout ||
      !hasItems,
  );

  const issueBanner = useMemo(
    () =>
      getIssueBanner({
        unavailableCount,
        priceChangedCount,
      }),
    [unavailableCount, priceChangedCount],
  );

  const handleQtyDraftChange = useCallback((itemId, value, maxQty) => {
    const safeItemId = safeText(itemId);
    if (!safeItemId) return;

    const sanitized = String(value || "").replace(/[^\d]/g, "");

    if (!sanitized) {
      setQtyDrafts((prev) => ({
        ...prev,
        [safeItemId]: "",
      }));
      return;
    }

    const clamped = clampQty(sanitized, 1, maxQty);

    setQtyDrafts((prev) => ({
      ...prev,
      [safeItemId]: String(clamped),
    }));
  }, []);

  const commitQty = useCallback(
    async (item) => {
      const itemId = safeText(item?._id);
      if (!itemId) return;

      const raw = qtyDrafts[itemId];
      const currentQty = toWholeQty(item?.qty, 1);
      const maxQty = Math.max(1, getMaxQty(item));
      const nextQty = clampQty(raw, 1, maxQty);

      if (!itemCanEditQty(item)) {
        setQtyDrafts((prev) => ({
          ...prev,
          [itemId]: String(currentQty),
        }));
        return;
      }

      if (nextQty === currentQty) {
        setQtyDrafts((prev) => ({
          ...prev,
          [itemId]: String(currentQty),
        }));
        return;
      }

      try {
        await dispatch(
          setCartItemQty({
            itemId,
            qty: nextQty,
          }),
        );
      } catch {
        setQtyDrafts((prev) => ({
          ...prev,
          [itemId]: String(currentQty),
        }));
      }
    },
    [dispatch, qtyDrafts],
  );

  const handleIncrement = useCallback(
    async (itemId) => {
      const safeItemId = safeText(itemId);
      if (!safeItemId) return;

      try {
        await dispatch(incrementCartItem(safeItemId));
      } catch {
        // Reducer + toast effect handle this.
      }
    },
    [dispatch],
  );

  const handleDecrement = useCallback(
    async (itemId) => {
      const safeItemId = safeText(itemId);
      if (!safeItemId) return;

      try {
        await dispatch(decrementCartItem(safeItemId));
      } catch {
        // Reducer + toast effect handle this.
      }
    },
    [dispatch],
  );

  const handleRemove = useCallback(
    async (itemId) => {
      const safeItemId = safeText(itemId);
      if (!safeItemId) return;

      try {
        await dispatch(removeCartItem(safeItemId));
      } catch {
        // Reducer + toast effect handle this.
      }
    },
    [dispatch],
  );

  const handleRefresh = useCallback(async () => {
    try {
      await dispatch(refreshCart());
    } catch {
      // Reducer + toast effect handle this.
    }
  }, [dispatch]);

  const handleClear = useCallback(async () => {
    const confirmed = window.confirm("Clear your entire basket?");
    if (!confirmed) return;

    try {
      await dispatch(clearCart());
    } catch {
      // Reducer + toast effect handle this.
    }
  }, [dispatch]);

  const handleRemoveUnavailable = useCallback(async () => {
    try {
      await dispatch(removeUnavailableItems());
    } catch {
      // Reducer + toast effect handle this.
    }
  }, [dispatch]);

  const handleRemovePriceChanged = useCallback(async () => {
    try {
      await dispatch(removePriceChangedItems());
    } catch {
      // Reducer + toast effect handle this.
    }
  }, [dispatch]);

  const handleProceedToCheckout = useCallback(async () => {
    try {
      const result = await dispatch(validateCart());

      if (result?.ok) {
        navigate("/checkout/shipping");
        return;
      }

      if (result?.empty) {
        toast.info("Your basket is empty.");
        return;
      }

      toast.warn(
        result?.message ||
          "Please resolve basket issues before continuing to checkout.",
      );
    } catch (err) {
      toast.error(
        getSafeUserMessage(
          err?.message,
          "We could not check your basket right now. Please try again later.",
        ),
      );
    }
  }, [dispatch, navigate]);

  if (loading && !cart) {
    return (
      <>
        <Meta title="Your basket | Academy Costumes London" />
        <CartMobileSkeleton />
      </>
    );
  }

  return (
    <main className="cart-mobile">
      <Meta
        title="Your basket | Academy Costumes London"
        description="Review your selected pieces before continuing to checkout."
        url="/cart"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Basket", url: "/cart" },
        ]}
      />


      <header className="cart-mobile__header">
        <p>Academy Costumes London</p>
        <h1>Basket</h1>
        <span>Review your selected pieces before checkout.</span>
      </header>

      {!hasItems ? (
        <EmptyCartMobileState />
      ) : (
        <>
          <CartMobileIssueBanner
            banner={issueBanner}
            cartBusy={cartBusy}
            pending={pending}
            unavailableCount={unavailableCount}
            priceChangedCount={priceChangedCount}
            canRemoveUnavailable={
              resolvedSummary?.ui?.actions?.canRemoveUnavailableItems
            }
            canRemovePriceChanged={
              resolvedSummary?.ui?.actions?.canRemovePriceChangedItems
            }
            onRemoveUnavailable={handleRemoveUnavailable}
            onRemovePriceChanged={handleRemovePriceChanged}
          />

          <section className="cart-mobile__items" aria-label="Basket items">
            {items.map((item) => {
              const itemId = safeText(item?._id);
              const itemBusy = Boolean(pending?.itemIds?.[itemId]) || cartBusy;
              const draftQty = qtyDrafts[itemId] ?? String(item.qty || 1);

              return (
                <CartMobileLineItem
                  key={`${item.lineKey || "line"}-${itemId}`}
                  item={item}
                  currency={currency}
                  draftQty={draftQty}
                  itemBusy={itemBusy}
                  onDraftChange={handleQtyDraftChange}
                  onCommitQty={commitQty}
                  onIncrement={handleIncrement}
                  onDecrement={handleDecrement}
                  onRemove={handleRemove}
                />
              );
            })}
          </section>

          <CartMobileSummary
            summary={resolvedSummary}
            currency={currency}
            unavailableCount={unavailableCount}
            priceChangedCount={priceChangedCount}
            checkoutDisabled={checkoutDisabled}
            cartBusy={cartBusy}
            pending={pending}
            validation={validation}
            onCheckout={handleProceedToCheckout}
            onRefresh={handleRefresh}
            onClear={handleClear}
          />

          <CartMobileStickyBar
            summary={resolvedSummary}
            currency={currency}
            checkoutDisabled={checkoutDisabled}
            onCheckout={handleProceedToCheckout}
          />
        </>
      )}
    </main>
  );
}
